import { trackConfig, type IFramework } from '@/framework';
import { getUtf8ByteLength, type TextSelectionRange } from '@/utils/text-kit';
import { LayerType, type ILayerProps, type ITextLayerProps, type Optional } from '@/version-types';
import {
    applySnapshotSize,
    applyTextRangeAttrs,
    pushHistory,
    unifyTextContentsAttrs,
    updateLayer,
    validateInputLayer,
} from '../utils';
import { getTextLayerHost } from '../utils/host';
import { type IShapeText } from './shape-text';

type DecorationKind = 'underline' | 'lineThrough';
type Options = { range?: Optional<TextSelectionRange> };
type Result = Promise<{ layer: ILayerProps; range: TextSelectionRange | null } | void>;

export interface ITextDecoration {
    /**
     * 设置下划线
     * @param layerID - 图层 id
     * @param flag - 是否设置下划线
     * @param options - 配置项
     * @returns
     */
    setUnderline(layerID: string, flag: boolean, options?: Options): Result;

    /**
     * 设置删除线
     * @param layerID - 图层 id
     * @param flag - 是否设置删除线
     * @param options - 配置项
     * @returns
     */
    setLineThrough(layerID: string, flag: boolean, options?: Options): Result;
}

export class TextDecoration implements ITextDecoration {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private resolveConflict(
        layerData: ITextLayerProps,
        kind: DecorationKind,
        flag: boolean,
        range: TextSelectionRange | null,
    ) {
        if (!flag) return layerData;

        const conflictType = kind === 'underline' ? 'lineThrough' : 'underline';
        const nextLayerData = { ...layerData };

        nextLayerData[conflictType] = false;

        const { textContents = [] } = layerData;
        if (!range && textContents.length > 0) {
            nextLayerData.textContents = unifyTextContentsAttrs(textContents, { [conflictType]: false });
        }

        if (!range || textContents.length === 0) return nextLayerData;

        const { start, end } = range;
        let cursor = 0;
        nextLayerData.textContents = textContents.map((item) => {
            const spanStart = cursor;
            cursor += getUtf8ByteLength(item.text);
            if (cursor <= start || spanStart >= end) return item;
            return {
                ...item,
                [conflictType]: false,
            };
        });

        return nextLayerData;
    }

    /**
     * 使用真实 Text Layer 宿主为文字装饰候选重新排版。
     *
     * @param prevLayer - 修改前的 Text Layer。
     * @param kind - 本次修改的装饰类型。
     * @param flag - 是否启用该装饰。
     * @param range - 可选的编辑选区。
     * @returns 可提交的图层与选区；宿主尚未就绪时返回 null。
     */
    private prepare(prevLayer: ILayerProps, kind: DecorationKind, flag: boolean, range: TextSelectionRange | null) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        const { texture, textGradient } = prevLayerData;

        // 使用纹理时不允许设置下划线和删除线
        if (texture && texture.url) return { layer: prevLayer, range: null };

        // 使用渐变时不允许设置下划线和删除线
        if (textGradient && textGradient.colors.length > 1) return { layer: prevLayer, range: null };

        let nextLayerData: ITextLayerProps = { ...prevLayerData };
        let nextRange: TextSelectionRange | null = null;

        if (range && range.start !== range.end) {
            const result = applyTextRangeAttrs(nextLayerData, range, {
                key: kind,
                value: flag,
            });
            nextLayerData = { ...nextLayerData, textContents: result.textContents };
            nextRange = result.range;
        } else {
            const { textContents: prevTextContents = [] } = prevLayerData;
            if (prevTextContents.length > 0) {
                const nextTextContents = unifyTextContentsAttrs(prevTextContents, { [kind]: flag });
                nextLayerData = { ...nextLayerData, textContents: nextTextContents };

                // 有 textContents 时，不设置全局属性的 lineThrough、underline
                // 否则渲染层上会出现重复的下划线或删除线
                if (flag) nextLayerData = { ...nextLayerData, [kind]: false };
            } else {
                nextLayerData = { ...nextLayerData, [kind]: flag };
            }
        }

        // resolve conflict
        nextLayerData = this.resolveConflict(nextLayerData, kind, flag, nextRange);

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (shapeExtension) return { layer: nextLayer, range: nextRange };

        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
        // 只有 ready snapshot 可以提交当前装饰属性对应的尺寸。
        if (resolution?.status !== 'ready') return null;
        const snapshot = resolution.value;

        nextLayer = applySnapshotSize(nextLayer, snapshot);

        return {
            layer: nextLayer,
            range: nextRange,
        };
    }

    private setGroup(prevLayer: ILayerProps, kind: DecorationKind, flag: boolean) {
        const children = prevLayer.children;
        if (children.length === 0) return;

        const layers: ILayerProps[] = [];
        for (const child of children) {
            const result = this.prepare(child, kind, flag, null);
            if (result === null) return { layer: prevLayer, range: null };
            layers.push(result.layer);
        }

        const nextLayer = { ...prevLayer, children: layers };
        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return { layer: nextLayer, range: null };
    }

    private async setSingle(
        prevLayer: ILayerProps,
        kind: DecorationKind,
        flag: boolean,
        range: TextSelectionRange | null,
    ) {
        const result = this.prepare(prevLayer, kind, flag, range);
        if (result === null) return { layer: prevLayer, range };

        let nextLayer = result.layer;

        const { shapeExtension } = prevLayer.data as ITextLayerProps;
        if (shapeExtension) {
            const layer = await this.shapeText.refreshShapeTextRender(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return { layer: nextLayer, range: result.range };
    }

    private async set(layerID: string, kind: DecorationKind, flag: boolean, options: Options) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const { range = null } = options;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            return this.setGroup(prevLayer, kind, flag);
        }

        return this.setSingle(prevLayer, kind, flag, range);
    }

    async setUnderline(layerID: string, flag: boolean, options = { range: null }) {
        return this.set(layerID, 'underline', flag, options);
    }

    async setLineThrough(layerID: string, flag: boolean, options = { range: null }) {
        return this.set(layerID, 'lineThrough', flag, options);
    }
}
